/**
 * Analytics consent for Google Analytics (Consent Mode v2).
 *
 * - EEA, UK and Switzerland: analytics cookies stay off until the visitor
 *   accepts. GA still receives cookieless pings, so totals are modelled.
 * - Everywhere else: analytics runs by default and the visitor can opt out
 *   (notice + opt-out, as Canadian privacy law allows for analytics).
 * - Advertising storage is denied everywhere; the site runs no ads.
 *
 * The visitor's choice lives in the `analyticsConsent` cookie ("granted" or
 * "denied"). The GA loader in App.js reads it before `config`, and the cookie
 * banner in Footer.js writes it through `setAnalyticsConsent`.
 */
import Cookies from 'js-cookie';

export const CONSENT_COOKIE = 'analyticsConsent';
export const CONSENT_EVENT = 'mediasfu:analytics-consent';

// EU/EEA member states, the UK and Switzerland (ISO 3166-1 alpha-2).
export const CONSENT_REQUIRED_REGIONS = [
  'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 'HU',
  'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SK', 'SI', 'ES',
  'SE', 'IS', 'LI', 'NO', 'GB', 'CH',
];

// Non-"Europe/" zones used by EEA territories (Iceland, Canaries, Madeira,
// Azores, Faroe, Svalbard).
const CONSENT_EXTRA_ZONES = new Set([
  'Atlantic/Reykjavik', 'Atlantic/Canary', 'Atlantic/Madeira', 'Atlantic/Azores',
  'Atlantic/Faroe', 'Arctic/Longyearbyen',
]);

/**
 * Whether a time zone may be in the EEA, UK or Switzerland. Deliberately
 * broad: every `Europe/*` zone counts, and so does an unknown or UTC-only
 * zone. It only decides whether we show the prompt; Google's own IP-based
 * region defaults (consentBootstrapScript) still keep cookies off in those
 * regions until the visitor accepts.
 */
export function isConsentTimeZone(timeZone, offsetMinutes) {
  const named = typeof timeZone === 'string' && timeZone && timeZone !== 'UTC' && !timeZone.startsWith('Etc/');
  if (named) return timeZone.startsWith('Europe/') || CONSENT_EXTRA_ZONES.has(timeZone);
  // Some browsers and privacy modes hide the zone name. Fall back to the UTC
  // offset (Date#getTimezoneOffset: minutes behind UTC): Europe spans roughly
  // UTC-1 (Azores) to UTC+4, so only offsets in that band ask.
  if (typeof offsetMinutes === 'number' && Number.isFinite(offsetMinutes)) {
    return offsetMinutes >= -240 && offsetMinutes <= 60;
  }
  return true;
}

/** The visitor's region needs an explicit choice before analytics cookies. */
export function needsConsentPrompt() {
  try {
    return isConsentTimeZone(
      Intl.DateTimeFormat().resolvedOptions().timeZone,
      new Date().getTimezoneOffset()
    );
  } catch (_) {
    return true;
  }
}

export function readAnalyticsConsent() {
  try {
    const value = Cookies.get(CONSENT_COOKIE);
    return value === 'granted' || value === 'denied' ? value : null;
  } catch (_) {
    return null;
  }
}

export function setAnalyticsConsent(value) {
  if (value !== 'granted' && value !== 'denied') return;
  try {
    Cookies.set(CONSENT_COOKIE, value, { expires: 365, sameSite: 'Lax' });
  } catch (_) {
    // Cookies can be blocked; the choice still applies to this page view.
  }
  if (typeof window !== 'undefined') {
    if (typeof window.gtag === 'function') {
      window.gtag('consent', 'update', { analytics_storage: value });
    }
    if (value === 'denied') removeAnalyticsCookies();
    window.dispatchEvent(new CustomEvent(CONSENT_EVENT, { detail: value }));
  }
}

/**
 * GA4 keeps `_ga` (visitor ID, up to 2 years) and `_ga_<ID>` (session state)
 * on the site's registrable domain. Denying consent stops new ones; this also
 * removes any set before the visitor changed their mind.
 */
export function removeAnalyticsCookies() {
  if (typeof document === 'undefined') return;
  const host = window.location.hostname;
  const parts = host.split('.');
  const domains = [undefined, host, `.${host}`];
  if (parts.length > 2) domains.push(`.${parts.slice(-2).join('.')}`);
  document.cookie
    .split(';')
    .map((entry) => entry.split('=')[0].trim())
    .filter((name) => name === '_ga' || name.startsWith('_ga_'))
    .forEach((name) => {
      domains.forEach((domain) => {
        try {
          Cookies.remove(name, domain ? { path: '/', domain } : { path: '/' });
        } catch (_) {
          // Ignore domains the browser won't let us address.
        }
      });
    });
}

export function clearAnalyticsConsent() {
  try {
    Cookies.remove(CONSENT_COOKIE);
  } catch (_) {
    // Nothing to clear.
  }
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(CONSENT_EVENT, { detail: null }));
  }
}

/**
 * Inline gtag commands that must run before `config`: regional defaults, then
 * the visitor's saved choice. Returned as a string for the loader's <script>.
 */
export function consentBootstrapScript() {
  const regions = JSON.stringify(CONSENT_REQUIRED_REGIONS);
  return `
    gtag('consent', 'default', {
      ad_storage: 'denied', ad_user_data: 'denied', ad_personalization: 'denied',
      analytics_storage: 'denied', region: ${regions}
    });
    gtag('consent', 'default', {
      ad_storage: 'denied', ad_user_data: 'denied', ad_personalization: 'denied',
      analytics_storage: 'granted'
    });
    gtag('set', 'ads_data_redaction', true);
    var consentMatch = document.cookie.match(/(?:^|; )${CONSENT_COOKIE}=(granted|denied)/);
    if (consentMatch) gtag('consent', 'update', { analytics_storage: consentMatch[1] });
  `;
}
